import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/controls/command";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import useHeraldChatHistoryStore from "@/store/herald-chat/herald-chat-history.slice";
import { ChatSearchResultItem } from "./ChatSearchResultItem";
import { useSearchChatConversations } from "./useSearchChatConversations";

export interface ChatSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PREVIOUS_CHATS_LIMIT = 25;
/** Sentinel so cmdk does not auto-highlight the first previous chat. */
const NO_SELECTION_VALUE = "__chat-search-no-selection__";

const COMMAND_GROUP_HEADING_CLASS_NAME =
  "px-0 **:[[cmdk-group-heading]]:px-5 **:[[cmdk-group-heading]]:pb-2 **:[[cmdk-group-heading]]:pt-5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:leading-5 **:[[cmdk-group-heading]]:tracking-normal **:[[cmdk-group-heading]]:text-gray-600";

const ChatSearchDialog = ({ open, onOpenChange }: ChatSearchDialogProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const { results, isSearching, hasActiveQuery } = useSearchChatConversations({
    query,
    enabled: open,
  });
  const allPreviousConversations = useHeraldChatHistoryStore(
    (state) => state.allPreviousConversations,
  );

  useEffect(() => {
    if (!open) {
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeSearchWhenHidden = () => {
      if (document.visibilityState !== "visible") {
        onOpenChange(false);
      }
    };

    document.addEventListener("visibilitychange", closeSearchWhenHidden);
    return () => {
      document.removeEventListener("visibilitychange", closeSearchWhenHidden);
    };
  }, [open, onOpenChange]);

  const handleSelectConversation = (conversationId: string) => {
    void navigate({
      to: "/chat/$conversationId",
      params: { conversationId },
    });
    onOpenChange(false);
  };

  const displaySearchResults = () => {
    if (!hasActiveQuery) {
      const previousConversations = allPreviousConversations.slice(
        0,
        PREVIOUS_CHATS_LIMIT,
      );

      if (previousConversations.length === 0) {
        return null;
      }

      return (
        <CommandGroup>
          <CommandItem
            value={NO_SELECTION_VALUE}
            className="hidden"
            aria-hidden
          />
          {previousConversations.map((conversation) => (
            <ChatSearchResultItem
              key={conversation.id}
              conversation={{
                id: conversation.id,
                updatedAt: conversation.updatedAt,
                searchMatch: null,
                title: conversation.title,
              }}
              onSelect={handleSelectConversation}
            />
          ))}
        </CommandGroup>
      );
    }

    if (isSearching || results.length > 0) {
      return (
        <CommandGroup
          heading={isSearching ? "Searching..." : "Search results"}
          className={COMMAND_GROUP_HEADING_CLASS_NAME}
        >
          {isSearching ? (
            <div className="flex h-7 items-center gap-3 px-5">
              <Skeleton className="size-3.5 shrink-0 rounded-full bg-gray-200" />
              <Skeleton className="h-3 max-w-md flex-1 rounded-full bg-gray-200" />
            </div>
          ) : (
            results.map((conversation) => (
              <ChatSearchResultItem
                key={conversation.id}
                conversation={conversation}
                onSelect={handleSelectConversation}
              />
            ))
          )}
        </CommandGroup>
      );
    }

    return (
      <CommandEmpty className="px-5 pt-5 pb-2 text-left text-xs font-medium leading-5 tracking-normal text-gray-600">
        No results
      </CommandEmpty>
    );
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search chats"
      className={[
        "h-96 min-h-0 w-full min-w-0 max-w-xl overflow-hidden border-0 p-0",
        "rounded-md shadow-[0px_10px_15px_-3px_rgba(22,22,22,0.08),0px_4px_6px_-2px_rgba(22,22,22,0.05)] sm:rounded-md",
        "**:data-[slot=command]:rounded-md",
        "**:data-[slot=command-input-wrapper]:h-16! **:data-[slot=command-input-wrapper]:gap-4! **:data-[slot=command-input-wrapper]:rounded-none! **:data-[slot=command-input-wrapper]:border-x-0! **:data-[slot=command-input-wrapper]:border-b! **:data-[slot=command-input-wrapper]:border-t-0! **:data-[slot=command-input-wrapper]:border-gray-200! **:data-[slot=command-input-wrapper]:px-5!",
        "[&_[data-slot=command-input-wrapper]>svg]:size-4! [&_[data-slot=command-input-wrapper]>svg]:text-gray-600! [&_[data-slot=command-input-wrapper]>svg]:opacity-100!",
        "[&_[cmdk-item]_svg]:size-3.5! [&_[cmdk-item]_svg]:text-gray-600!",
        "[&_[role=dialog]>button]:right-3.5 [&_[role=dialog]>button]:top-3.5 [&_[role=dialog]>button]:text-gray-600 [&_[role=dialog]>button_svg]:size-3.5",
      ].join(" ")}
      shouldFilter={false}
    >
      <CommandInput
        placeholder="Search chats..."
        value={query}
        onValueChange={setQuery}
        autoFocus
        className="text-sm leading-4 tracking-normal text-gray-900 placeholder:text-gray-600"
      />
      <CommandList className="max-h-none flex-1 py-1">
        {displaySearchResults()}
      </CommandList>
    </CommandDialog>
  );
};

export default ChatSearchDialog;
